// components/orders/EditOrderModal.js
import React, { useState, useMemo } from 'react';
import { formatRestaurantPrice, normalizeCurrency } from '../../utils/formatPrice';

export default function EditOrderModal({
  order,
  menuItems,
  categories,
  menuCurrency = 'UZS',
  onClose,
  onSubmit,
  loading,
}) {
  const cur = normalizeCurrency(menuCurrency);
  const [cart, setCart] = useState(() => {
    const init = {};
    (order.items || []).forEach((item) => {
      const id = item.menu_item_id ?? menuItems.find((m) => (m.name_ru || m.name) === item.name)?.id;
      if (id) init[id] = item.quantity;
    });
    return init;
  });

  const [search, setSearch]       = useState('');
  const [catFilter, setCatFilter] = useState('all');

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return menuItems.filter((i) => {
      const matchCat = catFilter === 'all' || i.category_id === catFilter;
      const matchQ   = (i.name_ru || i.name || '').toLowerCase().includes(q);
      return matchCat && matchQ;
    });
  }, [menuItems, search, catFilter]);

  const addItem = (item) =>
    setCart((prev) => ({ ...prev, [item.id]: (prev[item.id] || 0) + 1 }));

  const changeQty = (id, delta) => {
    setCart((prev) => {
      const next = (prev[id] || 0) + delta;
      if (next <= 0) {
        const { [id]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: next };
    });
  };

  const cartItems    = menuItems.filter((i) => cart[i.id] > 0);
  const totalAmount  = cartItems.reduce((s, i) => s + i.price * cart[i.id], 0);
  const hasChanges   = cartItems.length > 0;

  const handleSubmit = () => {
    if (!hasChanges) return;
    onSubmit({
      orderId: order.id,
      items: cartItems.map((i) => ({ menuItemId: i.id, quantity: cart[i.id] })),
    });
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    }} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{
        backgroundColor: '#fff', borderRadius: '20px 20px 0 0',
        width: '100%', maxWidth: '600px',
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        padding: '20px', boxSizing: 'border-box',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: '4px' }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>
            ✏️ Заказ #{order.order_number}
          </h2>
          <button onClick={onClose} style={{
            width: '32px', height: '32px', borderRadius: '50%',
            border: 'none', backgroundColor: '#F0F0F0',
            fontSize: '18px', cursor: 'pointer',
          }}>×</button>
        </div>
        <div style={{ fontSize: '13px', color: '#888', marginBottom: '16px' }}>
          🪑 Стол: {order.table_number || '—'}
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>

          {categories.length > 0 && (
            <div style={{
              display: 'flex', gap: '6px', overflowX: 'auto',
              paddingBottom: '8px', marginBottom: '10px', scrollbarWidth: 'none',
            }}>
              <button onClick={() => setCatFilter('all')} style={catBtn(catFilter === 'all')}>
                Все
              </button>
              {categories.map((c) => (
                <button key={c.id} onClick={() => setCatFilter(c.id)}
                  style={catBtn(catFilter === c.id)}>
                  {c.name_ru || c.name}
                </button>
              ))}
            </div>
          )}

          <input
            placeholder="🔍 Поиск по названию..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '10px 12px', borderRadius: '10px',
              border: '1px solid #E0E0E0', fontSize: '14px',
              outline: 'none', boxSizing: 'border-box', marginBottom: '10px',
            }}
          />

          <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '14px' }}>
            {filtered.length === 0 && (
              <div style={{ textAlign: 'center', color: '#999', padding: '20px', fontSize: '13px' }}>
                Ничего не найдено
              </div>
            )}
            {filtered.map((item) => (
              <div key={item.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 0', borderBottom: '1px solid #F5F5F5',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '14px', fontWeight: '500' }}>
                    {item.name_ru || item.name}
                  </div>
                  <div style={{ fontSize: '12px', color: '#888' }}>
                    {formatRestaurantPrice(item.price, cur)}
                  </div>
                </div>
                {cart[item.id] ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button onClick={() => changeQty(item.id, -1)} style={qBtn('#F0F0F0', '#333')}>−</button>
                    <span style={{ fontSize: '14px', fontWeight: '700',
                      minWidth: '20px', textAlign: 'center' }}>
                      {cart[item.id]}
                    </span>
                    <button onClick={() => changeQty(item.id, +1)} style={qBtn('#000', '#fff')}>+</button>
                  </div>
                ) : (
                  <button onClick={() => addItem(item)} style={{
                    padding: '6px 14px', borderRadius: '8px', border: 'none',
                    backgroundColor: '#000', color: '#fff',
                    fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                  }}>+ Добавить</button>
                )}
              </div>
            ))}
          </div>

          <div style={{
            backgroundColor: '#F8F8F8', borderRadius: '14px',
            padding: '12px', marginBottom: '14px',
          }}>
            <div style={{ fontSize: '13px', fontWeight: '700', marginBottom: '8px', color: '#555' }}>
              🛒 Состав заказа:
            </div>
            {cartItems.length === 0 && (
              <div style={{ fontSize: '13px', color: '#E85D4A', textAlign: 'center', padding: '8px' }}>
                ⚠️ Добавьте хотя бы одно блюдо
              </div>
            )}
            {cartItems.map((item) => (
              <div key={item.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '6px 0', borderBottom: '1px solid #EFEFEF',
              }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: '13px' }}>{item.name_ru || item.name}</span>
                  <span style={{ fontSize: '12px', color: '#999', marginLeft: '6px' }}>
                    × {cart[item.id]}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>
                    {formatRestaurantPrice(item.price * cart[item.id], cur)}
                  </span>
                  <button onClick={() => changeQty(item.id, -1)} style={qBtn('#FFEBEE', '#E85D4A')}>−</button>
                  <button onClick={() => changeQty(item.id, +1)} style={qBtn('#E8F5E9', '#34C759')}>+</button>
                </div>
              </div>
            ))}
            {cartItems.length > 0 && (
              <div style={{
                display: 'flex', justifyContent: 'space-between',
                fontWeight: '700', fontSize: '15px',
                paddingTop: '10px', marginTop: '4px',
              }}>
                <span>Итого:</span>
                <span>{formatRestaurantPrice(totalAmount, cur)}</span>
              </div>
            )}
          </div>
        </div>

        <div style={{
          display: 'flex', gap: '10px',
          paddingTop: '12px', borderTop: '1px solid #F0F0F0',
          paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
        }}>
          <button onClick={onClose} style={{
            flex: 1, padding: '14px', borderRadius: '12px', border: 'none',
            backgroundColor: '#F0F0F0', color: '#333',
            fontSize: '15px', fontWeight: '600', cursor: 'pointer',
          }}>Отмена</button>
          <button
            onClick={handleSubmit}
            disabled={!hasChanges || loading}
            style={{
              flex: 2, padding: '14px', borderRadius: '12px', border: 'none',
              backgroundColor: !hasChanges ? '#ccc' : '#007AFF',
              color: '#fff', fontSize: '15px', fontWeight: '700',
              cursor: !hasChanges ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? '⏳ Сохраняем...' : `💾 Сохранить · ${formatRestaurantPrice(totalAmount, cur)}`}
          </button>
        </div>
      </div>
    </div>
  );
}

const qBtn   = (bg, color) => ({
  width: '28px', height: '28px', borderRadius: '8px',
  border: 'none', backgroundColor: bg, color,
  fontSize: '16px', fontWeight: '700', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
});

const catBtn = (active) => ({
  flexShrink: 0, padding: '5px 12px', borderRadius: '100px', border: 'none',
  backgroundColor: active ? '#000' : '#F0F0F0',
  color: active ? '#fff' : '#555',
  fontSize: '12px', fontWeight: '600', cursor: 'pointer',
});
